import { useQuery } from '@tanstack/react-query'

import { Alert, Detail, InlineLoader } from '../../components/ui'
import { fetchAIRun } from '../../api/airuns'
import { keys } from '../../api/keys'
import { t } from '../../i18n'
import { fullTime, money } from '../../format'

/**
 * Which model produced this draft, on which prompt, at what cost.
 *
 * It is fetched only when somebody opens it. Most drafts are approved without
 * anybody asking, and a request per draft on every screen would buy nothing.
 */
export function AIRunPanel({ runId }: { readonly runId: string }) {
  const run = useQuery({
    queryKey: keys.aiRun(runId),
    queryFn: ({ signal }) => fetchAIRun(runId, signal),
    // A finished run never changes, so re-reading it on every render of the
    // conversation would be pure noise.
    staleTime: Infinity,
  })

  if (run.isPending) return <InlineLoader />
  if (run.isError) return <Alert tone="error">{t('aiRun.failed')}</Alert>

  return (
    <div className="airun">
      <dl className="details details--tight">
        <Detail term={t('aiRun.task')}>{run.data.task}</Detail>
        <Detail term={t('aiRun.provider')}>{run.data.provider}</Detail>
        <Detail term={t('aiRun.model')}>{run.data.model}</Detail>
        {run.data.promptVersion === '' ? null : (
          <Detail term={t('aiRun.promptVersion')}>{run.data.promptVersion}</Detail>
        )}
        {run.data.schemaVersion === '' ? null : (
          <Detail term={t('aiRun.schemaVersion')}>{run.data.schemaVersion}</Detail>
        )}
        <Detail term={t('aiRun.tokens')}>
          {run.data.tokens === null
            ? t('aiRun.tokensUnknown')
            : t('aiRun.tokensValue', { input: run.data.tokens.input, output: run.data.tokens.output })}
        </Detail>
        <Detail term={t('aiRun.cost')}>{money(run.data.cost)}</Detail>
        {run.data.latencyMs === undefined ? null : (
          <Detail term={t('aiRun.latency')}>{t('aiRun.latencyValue', { ms: run.data.latencyMs })}</Detail>
        )}
        <Detail term={t('aiRun.status')}>{run.data.status}</Detail>
        {run.data.error === '' ? null : <Detail term={t('aiRun.error')}>{run.data.error}</Detail>}
        <Detail term={t('aiRun.createdAt')}>{fullTime(run.data.createdAt)}</Detail>
      </dl>
      <p className="airun__note">{t('aiRun.noText')}</p>
    </div>
  )
}
